import React, { useContext } from 'react';
import { Form, Input, Button } from "antd"
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../context/AppContext';

interface LoginCredentials{
    email: string;
    confirmPassword: string;
}

export const Login = () => {
    const context = useContext(AppContext);
    const [loading, setLoading] = React.useState(false)
    const { onLogin } = context;
    const [form] = Form.useForm();
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const smdcLogo = require('../assets/images/smdc-logo-white.png');

    const backgroundStyle = {
        height: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #92A4FF -36.6%, #032DFD 35.11%, #0723AD 101.27%)',
    };    

    const loginPanelStyle = {
        boxShadow: '-10px 12px 28px rgba(53, 54, 119, 0.37)',
        width: '450px',
    };

    const passwdIcon = (visible: boolean) => {
        return visible ? <EyeTwoTone style={{ fontSize: 20 }} /> : <EyeInvisibleOutlined  style={{ color: "#27272A", fontSize: 20, transform: "rotate(180deg) scaleX(-1)" }} />
    }

    const navigate = useNavigate();

    const onFinish = async (values: LoginCredentials) => {
        const payload = {
            email: values.email,
            password: values.confirmPassword,
        }
    
        try {
            setLoading(true);

            const response = await onLogin(payload);
    
            if (response) {
                navigate('/transactions');
            } else {
                setLoading(false);
                setErrorMessage('Invalid login');    
            }
        } catch (e) {
            console.error(e?.response?.data || e.message);
            setErrorMessage('Invalid login');
        } finally {
            setLoading(false);
        }
    };    

    const onValuesChange = () => {
        setErrorMessage(null);
    };

    return (
        <div style={backgroundStyle}>
            <div className="flex flex-col justify-center items-center h-screen">
                <img src={smdcLogo} width={150} height={80} alt="SMDC Express Pay Logo" />
                <div className="bg-neutralWhite-25 rounded-2xl login-panel p-11 mt-8" style={loginPanelStyle}>
                    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange}>
                        <h1 className="text-3xl text-neutralBlack mb-2 font-interSemiBold">
                            Welcome!
                        </h1>
                        <p className="text-body3 font-normal text-neutralBlack-75 mb-4 font-interRegular">
                            Please create your password to proceed
                        </p>

                        <label className="text-sm text-neutralGray font-medium font-interRegular required">
                            Company email
                        </label>
                        <Form.Item
                            name="email"
                            className="w-full"
                            rules={[{ required: true, message: 'Please input a valid email', type: 'email' }]}
                        >
                            <Input
                                className="h-12"
                                onChange={onValuesChange}
                            />
                        </Form.Item>

                        <label className="text-sm text-neutralGray font-medium font-interRegular required">
                            Password <span className='text-primary font-nunitoSansRegular' style={{ fontSize: 10, fontWeight: 700, verticalAlign: "top" }}>*</span>
                        </label>
                        <Form.Item name="confirmPassword" className="w-full" rules={[{ required: true, message: 'Please input your password' }]}>
                            <Input.Password
                                className="h-12"
                                iconRender={(visible) => passwdIcon(visible)}
                                onChange={onValuesChange}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Form.ErrorList className="text-red-500 mb-2 -mt-2 text-body3" errors={[errorMessage]} />

                            <Button
                                htmlType="submit"
                                className="w-full text-neutralWhite bg-primary text-base font-interMedium leading-6 border-primary hover:bg-primary-hovered hover:text-neutral-50 active:border-primary-border active:bg-primary-clicked py-2 mt-1 h-12"
                                loading={loading}
                                disabled={loading}
                            >
                                {loading ? 'Processing' : 'Proceed with this password'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};
